import React, { useEffect, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileSignature,
  faListOl,
  faLock,
  faMinus,
  faPen,
  faPlus,
} from "@fortawesome/free-solid-svg-icons";
import { useDispatch, useSelector } from "react-redux";
import UsedInBadge from "../UsedInBadge";
import HelpButton from "../../../HelpContent/HelpButton";
import MyCollapse from "../../../Common/MyCollapse";
import {
  deleteInputArgument,
  deleteVariableGroup,
  moveVariableGroup,
  selectInputArguments,
  selectVariableGroups,
  setInputArguments,
} from "../../../store/slices/projectSlice";
import { setCurrentModal } from "../../../store/slices/modalsSlice";
import Input from "../../nodes/Logic/Inputs/Input";

const DEFAULT_GROUP_UUID = "";
const INPUT_DROP_SLOT_DELAY_MS = 300;

export default function VariableGroups() {
  const variableGroups = useSelector(selectVariableGroups);
  const inputArguments = useSelector(selectInputArguments);
  const dispatch = useDispatch();
  const [variableFilter, setVariableFilter] = useState("");
  const [groupDragTarget, setGroupDragTarget] = useState();
  const [draggedGroupIndex, setDraggedGroupIndex] = useState();
  const [draggedInputUuid, setDraggedInputUuid] = useState();
  const [inputDropGroupUuid, setInputDropGroupUuid] = useState();
  const [inputDropSlot, setInputDropSlot] = useState();
  const [collapsedGroupUuids, setCollapsedGroupUuids] = useState(
    () => new Set()
  );
  const groupExpandTimeoutsRef = useRef({});
  const inputDropSlotTimeoutRef = useRef();
  const pendingInputDropSlotRef = useRef();

  const orderedGroups = useMemo(() => {
    const groups = [...variableGroups].sort((a, b) => a.Order - b.Order);
    if (!groups.some((group) => group.GroupUuid === DEFAULT_GROUP_UUID)) {
      return [
        {
          GroupUuid: DEFAULT_GROUP_UUID,
          Label: "Default",
          Order: -1,
        },
        ...groups,
      ];
    }
    return groups;
  }, [variableGroups]);

  const orderedInputArguments = useMemo(
    () => [...inputArguments].sort((a, b) => a.Order - b.Order),
    [inputArguments]
  );

  const knownGroupUuids = useMemo(
    () => new Set(variableGroups.map((group) => group.GroupUuid)),
    [variableGroups]
  );

  const filteredInputArguments = orderedInputArguments.filter(
    (arg) =>
      !variableFilter ||
      (variableFilter === "public" && arg.IsParameter) ||
      (variableFilter === "private" && !arg.IsParameter)
  );

  const getInputGroupUuid = (input) =>
    knownGroupUuids.has(input.GroupUuid) ? input.GroupUuid : DEFAULT_GROUP_UUID;

  const openVariableGroupModal = (variableGroupUUID) => {
    dispatch(
      setCurrentModal({
        modal: "variableGroup",
        tab: "create",
        data: { variableGroupUUID },
      })
    );
  };

  const openInputArgumentModal = (inputArgumentUUID, groupUuid) => {
    dispatch(
      setCurrentModal({
        modal: "inputArgument",
        tab: "create",
        data: { inputArgumentUUID, groupUuid },
      })
    );
  };

  const assignInputToGroup = (inputUuid, destinationGroupUuid, slotIndex) => {
    const draggedInput = orderedInputArguments.find(
      (input) => input.UUID === inputUuid
    );

    if (!draggedInput) return;

    const updatedInput = {
      ...draggedInput,
      GroupUuid: destinationGroupUuid,
    };
    const inputsWithoutDragged = orderedInputArguments.filter(
      (input) => input.UUID !== inputUuid
    );
    const sourceGroupUuid = getInputGroupUuid(draggedInput);
    const sourceGroupIndex = orderedInputArguments
      .filter((input) => getInputGroupUuid(input) === sourceGroupUuid)
      .findIndex((input) => input.UUID === inputUuid);
    const adjustedSlotIndex =
      sourceGroupUuid === destinationGroupUuid &&
      sourceGroupIndex >= 0 &&
      slotIndex !== undefined &&
      sourceGroupIndex < slotIndex
        ? slotIndex - 1
        : slotIndex;
    const destinationGroupInputs = inputsWithoutDragged.filter(
      (input) => getInputGroupUuid(input) === destinationGroupUuid
    );
    const insertBeforeInput =
      adjustedSlotIndex !== undefined
        ? destinationGroupInputs[adjustedSlotIndex]
        : undefined;
    const insertBeforeIndex = insertBeforeInput
      ? inputsWithoutDragged.findIndex(
          (input) => input.UUID === insertBeforeInput.UUID
        )
      : -1;
    const insertAfterIndex = inputsWithoutDragged.reduce(
      (lastIndex, input, index) =>
        getInputGroupUuid(input) === destinationGroupUuid ? index : lastIndex,
      -1
    );
    const nextInputs = [...inputsWithoutDragged];
    nextInputs.splice(
      insertBeforeIndex >= 0 ? insertBeforeIndex : insertAfterIndex + 1,
      0,
      updatedInput
    );

    dispatch(
      setInputArguments(
        nextInputs.map((input, index) => ({
          ...input,
          Order: index,
        }))
      )
    );
  };

  const clearInputDropSlotTimer = () => {
    if (inputDropSlotTimeoutRef.current) {
      clearTimeout(inputDropSlotTimeoutRef.current);
      inputDropSlotTimeoutRef.current = undefined;
    }
    pendingInputDropSlotRef.current = undefined;
  };

  const handleInputDrop = (event, groupUuid, slotIndex) => {
    event.preventDefault();
    event.stopPropagation();
    clearGroupExpandTimer(groupUuid);
    clearInputDropSlotTimer();

    if (draggedInputUuid) {
      assignInputToGroup(draggedInputUuid, groupUuid, slotIndex);
    }

    setDraggedInputUuid();
    setInputDropGroupUuid();
    setInputDropSlot();
  };

  const setInputDropTarget = (groupUuid, slotIndex, delayed = false) => {
    if (!delayed) {
      clearInputDropSlotTimer();
      setInputDropGroupUuid(groupUuid);
      setInputDropSlot({ groupUuid, slotIndex });
      return;
    }

    if (isInputDropSlot(groupUuid, slotIndex)) {
      return;
    }

    const pendingInputDropSlot = pendingInputDropSlotRef.current;
    if (
      pendingInputDropSlot?.groupUuid === groupUuid &&
      pendingInputDropSlot?.slotIndex === slotIndex
    ) {
      return;
    }

    clearInputDropSlotTimer();
    pendingInputDropSlotRef.current = { groupUuid, slotIndex };
    inputDropSlotTimeoutRef.current = setTimeout(() => {
      setInputDropGroupUuid(groupUuid);
      setInputDropSlot({ groupUuid, slotIndex });
      inputDropSlotTimeoutRef.current = undefined;
      pendingInputDropSlotRef.current = undefined;
    }, INPUT_DROP_SLOT_DELAY_MS);
  };

  const clearInputDropTarget = () => {
    clearInputDropSlotTimer();
    setInputDropGroupUuid();
    setInputDropSlot();
  };

  const setGroupOpen = (groupUuid, show) => {
    setCollapsedGroupUuids((previous) => {
      const nextCollapsedGroupUuids = new Set(previous);
      if (show) {
        nextCollapsedGroupUuids.delete(groupUuid);
      } else {
        nextCollapsedGroupUuids.add(groupUuid);
      }
      return nextCollapsedGroupUuids;
    });
  };

  const clearGroupExpandTimer = (groupUuid) => {
    if (groupExpandTimeoutsRef.current[groupUuid]) {
      clearTimeout(groupExpandTimeoutsRef.current[groupUuid]);
      delete groupExpandTimeoutsRef.current[groupUuid];
    }
  };

  const clearGroupExpandTimers = () => {
    Object.values(groupExpandTimeoutsRef.current).forEach(clearTimeout);
    groupExpandTimeoutsRef.current = {};
  };

  const scheduleGroupExpand = (groupUuid) => {
    if (
      !collapsedGroupUuids.has(groupUuid) ||
      groupExpandTimeoutsRef.current[groupUuid]
    ) {
      return;
    }

    groupExpandTimeoutsRef.current[groupUuid] = setTimeout(() => {
      setGroupOpen(groupUuid, true);
      delete groupExpandTimeoutsRef.current[groupUuid];
    }, 500);
  };

  useEffect(
    () => () => {
      clearGroupExpandTimers();
      clearInputDropSlotTimer();
    },
    []
  );

  const handleGroupDragEnd = (sourceIndex) => {
    if (
      draggedGroupIndex !== undefined &&
      groupDragTarget !== undefined &&
      groupDragTarget !== sourceIndex
    ) {
      dispatch(
        moveVariableGroup({
          sourceIndex,
          destinationIndex: groupDragTarget,
        })
      );
    }

    setGroupDragTarget();
    setDraggedGroupIndex();
  };

  const getGroupInputs = (groupUuid) =>
    filteredInputArguments.filter(
      (input) => getInputGroupUuid(input) === groupUuid
    );

  const isInputDropSlot = (groupUuid, slotIndex) =>
    inputDropSlot?.groupUuid === groupUuid &&
    inputDropSlot?.slotIndex === slotIndex;

  const renderInputDropSlot = (groupUuid, slotIndex) =>
    isInputDropSlot(groupUuid, slotIndex) ? (
      <div
        key={`drop-slot-${groupUuid}-${slotIndex}`}
        className="mx-3 my-1 py-2 border border-primary rounded bg-purple-gradient"
        onDragOver={(event) => {
          if (draggedInputUuid) {
            event.preventDefault();
            event.stopPropagation();
            setInputDropTarget(groupUuid, slotIndex);
          }
        }}
        onDrop={(event) => handleInputDrop(event, groupUuid, slotIndex)}
      >
        <span className="visually-hidden">Drop variable here</span>
      </div>
    ) : null;

  const getPointerSlotIndex = (event, inputIndex) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    return event.clientY < bounds.top + bounds.height / 2
      ? inputIndex
      : inputIndex + 1;
  };

  return (
    <div className="mt-2 card">
      <div className="card-header m-0 p-2 d-flex align-items-center flex-wrap">
        <h5 className="card-title mb-0">Variables</h5>
        <div className="ms-2">
          <Input
            inputType="radio"
            radioName="inputVariableFilter"
            value={[variableFilter]}
            onChange={([value]) => {
              setVariableFilter(value);
            }}
            options={[
              { value: "", label: "All" },
              { value: "public", label: "Public" },
              { value: "private", label: "Private" },
            ]}
          />
        </div>
        <HelpButton helpKey="variables" />
        <HelpButton helpKey="variable-groups" />
      </div>
      <div className="card-body p-0">
        {orderedGroups.length > 0 ? (
          <ul className="list-group list-group-flush">
            {orderedGroups.map((group, index) => {
              const groupInputs = getGroupInputs(group.GroupUuid);
              const isInputDropTarget = inputDropGroupUuid === group.GroupUuid;
              const hasInputs = groupInputs.length > 0;
              const isFallbackDefaultGroup = !variableGroups.some(
                (variableGroup) => variableGroup.GroupUuid === group.GroupUuid
              );

              return (
                <li
                  key={group.GroupUuid}
                  className={`list-group-item p-0 ${
                    isInputDropTarget ? "bg-light" : ""
                  }`}
                  onDragOver={(event) => {
                    if (draggedInputUuid) {
                      event.preventDefault();
                      if (hasInputs) {
                        clearInputDropSlotTimer();
                        setInputDropGroupUuid(group.GroupUuid);
                        setInputDropSlot();
                      } else {
                        setInputDropTarget(group.GroupUuid, 0, true);
                      }
                      return;
                    }

                    if (draggedGroupIndex !== undefined) {
                      event.preventDefault();
                      setGroupDragTarget(index);
                    }
                  }}
                  onDragLeave={() => {
                    clearInputDropSlotTimer();
                    if (inputDropGroupUuid === group.GroupUuid) {
                      clearInputDropTarget();
                    }
                    clearGroupExpandTimer(group.GroupUuid);
                  }}
                  onDrop={(event) => handleInputDrop(event, group.GroupUuid)}
                >
                  {groupDragTarget === index && (
                    <div className="p-1 bg-purple-gradient" />
                  )}
                  <div
                    className="m-0 py-1 px-2"
                    draggable
                    onDragStart={(event) => {
                      event.dataTransfer.effectAllowed = "move";
                      setDraggedGroupIndex(index);
                    }}
                    onDragEnd={() => handleGroupDragEnd(index)}
                  >
                    <MyCollapse
                      disabled={!hasInputs}
                      show={!collapsedGroupUuids.has(group.GroupUuid)}
                      onShowChange={(show) =>
                        setGroupOpen(group.GroupUuid, show)
                      }
                      headerProps={{
                        onDragOver: (event) => {
                          if (draggedInputUuid) {
                            event.preventDefault();
                            event.stopPropagation();
                            if (hasInputs) {
                              clearInputDropSlotTimer();
                              scheduleGroupExpand(group.GroupUuid);
                              setInputDropGroupUuid(group.GroupUuid);
                              setInputDropSlot();
                            } else {
                              setInputDropTarget(group.GroupUuid, 0, true);
                            }
                          }
                        },
                        onDragLeave: () => {
                          clearGroupExpandTimer(group.GroupUuid);
                        },
                      }}
                      heading={
                        <div className="d-flex align-items-center overflow-hidden">
                          <span className="text-truncate">{group.Label}</span>
                        </div>
                      }
                      headingActions={
                        <div className="d-flex align-items-center">
                          <button
                            type="button"
                            title="Edit variable group"
                            onClick={() =>
                              openVariableGroupModal(group.GroupUuid)
                            }
                            className="btn btn-outline-primary btn-sm border-0 px-1 ms-1"
                            style={{ width: 22 }}
                          >
                            <FontAwesomeIcon icon={faPen} />
                          </button>
                          {!isFallbackDefaultGroup && !hasInputs ? (
                            <button
                              type="button"
                              title="Delete variable group"
                              className="btn btn-outline-danger btn-sm border-0 px-1 ms-1"
                              style={{ width: 22 }}
                              onClick={() =>
                                dispatch(deleteVariableGroup(group.GroupUuid))
                              }
                            >
                              <FontAwesomeIcon icon={faMinus} />
                            </button>
                          ) : null}
                        </div>
                      }
                      actions={
                        <div className="ms-auto d-flex align-items-center">
                          <span
                            className="badge bg-secondary me-1"
                            title="Used in input arguments"
                          >
                            {groupInputs.length}
                          </span>
                          <button
                            type="button"
                            title="Add variable"
                            onClick={() =>
                              openInputArgumentModal(
                                "create_new",
                                group.GroupUuid
                              )
                            }
                            className="btn btn-outline-primary btn-sm border-0 px-1 me-1"
                            style={{ width: 22 }}
                          >
                            <FontAwesomeIcon icon={faPlus} />
                          </button>
                        </div>
                      }
                    >
                      {hasInputs ? (
                        <ul className="list-group list-group-flush border-top">
                          {renderInputDropSlot(group.GroupUuid, 0)}
                          {groupInputs.map((variable, inputIndex) => {
                            return (
                              <React.Fragment key={variable.UUID}>
                                <li
                                  draggable
                                  className="list-group-item py-1 px-2 ps-4"
                                  onDragStart={(event) => {
                                    event.stopPropagation();
                                    event.dataTransfer.effectAllowed = "move";
                                    setDraggedInputUuid(variable.UUID);
                                    setInputDropGroupUuid(group.GroupUuid);
                                    setInputDropSlot();
                                  }}
                                  onDragOver={(event) => {
                                    if (draggedInputUuid) {
                                      event.preventDefault();
                                      event.stopPropagation();
                                      setInputDropTarget(
                                        group.GroupUuid,
                                        getPointerSlotIndex(event, inputIndex),
                                        true
                                      );
                                    }
                                  }}
                                  onDrop={(event) =>
                                    handleInputDrop(
                                      event,
                                      group.GroupUuid,
                                      getPointerSlotIndex(event, inputIndex)
                                    )
                                  }
                                  onDragEnd={() => {
                                    clearGroupExpandTimers();
                                    clearInputDropSlotTimer();
                                    setDraggedInputUuid();
                                    setInputDropGroupUuid();
                                    setInputDropSlot();
                                  }}
                                >
                                  <div className="m-0 d-flex justify-content-between align-items-center">
                                    <button
                                      type="button"
                                      onClick={() =>
                                        openInputArgumentModal(variable.UUID)
                                      }
                                      className="btn btn-link text-start p-0"
                                    >
                                      {variable.Label}
                                    </button>
                                    <span
                                      className={`ms-auto badge me-1 ${
                                        variable.IsParameter
                                          ? "bg-colored"
                                          : "bg-secondary"
                                      }`}
                                    >
                                      {variable.IsMultiple && (
                                        <FontAwesomeIcon
                                          icon={faListOl}
                                          className="mx-1"
                                        />
                                      )}
                                      {variable.IsReadOnly && (
                                        <FontAwesomeIcon
                                          icon={faLock}
                                          className="mx-1"
                                        />
                                      )}
                                      {variable.IsParameter && (
                                        <FontAwesomeIcon
                                          icon={faFileSignature}
                                          className="mx-1"
                                        />
                                      )}
                                      {variable.Format}
                                    </span>
                                    <UsedInBadge
                                      arg={variable}
                                      alt={
                                        <button
                                          type="button"
                                          title="Delete argument"
                                          className="btn btn-outline-danger btn-sm border-0 px-1"
                                          style={{ width: 22 }}
                                          onClick={() =>
                                            dispatch(
                                              deleteInputArgument(variable.UUID)
                                            )
                                          }
                                        >
                                          <FontAwesomeIcon icon={faMinus} />
                                        </button>
                                      }
                                    />
                                  </div>
                                </li>
                                {renderInputDropSlot(
                                  group.GroupUuid,
                                  inputIndex + 1
                                )}
                              </React.Fragment>
                            );
                          })}
                        </ul>
                      ) : (
                        renderInputDropSlot(group.GroupUuid, 0)
                      )}
                    </MyCollapse>
                  </div>
                </li>
              );
            })}
          </ul>
        ) : (
          <div className="text-center m-1">No variables</div>
        )}
        <div className="p-2 border-top text-end">
          <button
            type="button"
            title="Add variable group"
            onClick={() => openVariableGroupModal("create_new")}
            className="btn btn-outline-primary btn-sm"
          >
            <FontAwesomeIcon icon={faPlus} className="me-1" />
            Add group
          </button>
        </div>
      </div>
    </div>
  );
}
